import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import Skeleton from 'react-loading-skeleton';

import { getGeneralInformationFilter, ProductDetailTerms } from '../ProductDetailTerms';
import { ProductAmountInputFormData } from '../components/ProductAmountInput';
import { ProductDetailMainTabAffiliate } from '../ProductDetailMainTab';

import { requestWallet } from 'common/features/store/duck/wallet/duck';
import { Product, ProductQueryParam, ProductType } from 'common/api/e-comm/models/Product';
import { useRootSelector } from 'rootStore';
import { useAddProductToCart } from 'hooks/useAddProductToCart';
import { useProductRebate } from 'hooks/useProductRebate';
import { useLocation, matchPath, useHistory } from 'react-router-dom';
import { faChevronDown } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { CartLineItemGiftDetails } from 'common/api/e-comm/models/CartLineItem';
import { selectIsShipToCoordinatorEnabled } from 'common/features/store/duck/organization/duck';
import { documentToReactComponents } from '@contentful/rich-text-react-renderer';
import { Document } from '@contentful/rich-text-types';
import { constructorInstance } from 'common/api/constructor/constructor-instance';
import { demonetize } from 'common/utils/strings';
import { DetailsProps } from '../components/utils/types';
import { ProductDetailScreenStyles } from '../ProductDetailScreenStyles';
import { FirstRow } from '../components/FirstRow';
import { Routes } from 'routes';
import { EarnTypeConstructorFilter } from 'common/api/constructor/util';
import { selectAffiliateEnabledWeb } from 'common/features/store/duck/ffs';
import useQueryParams from 'hooks/useQueryParams';

export const ProductDetailScreenContentConstructor = ({
    brand,
    brands,
    brandId,
    constructorBrand,
    products,
    isLoadingBrandInfo,
    isAffiliateOnlyBrand,
    isGiftCardOnlyBrand,
    backRoute,
    idWithSlug,
}: DetailsProps) => {
    const [readMoreExpanded, setReadMoreExpanded] = useState(false);
    const [selectedTab, setSelectedTab] = useState<ProductType>();
    const { pathname } = useLocation();
    const nav = useHistory();
    const params = useQueryParams();
    const isAffiliateEnabled = useRootSelector(selectAffiliateEnabledWeb);

    const [selectedWayToEarnTab, setSelectedWayToEarnTab] = useState<EarnTypeConstructorFilter>(
        EarnTypeConstructorFilter.GiftCards
    );

    const dispatch = useDispatch<any>();

    useEffect(() => {
        if (!isAffiliateEnabled) {
            return;
        }

        const shopOnlinePath = Routes.BrandsShopOnlineDetails;

        if (!matchPath(pathname, { path: shopOnlinePath, exact: true }) && !isAffiliateOnlyBrand) {
            setSelectedWayToEarnTab(EarnTypeConstructorFilter.GiftCards);
        } else {
            setSelectedWayToEarnTab(EarnTypeConstructorFilter.ShopOnline);
        }
    }, [isAffiliateEnabled, pathname, isAffiliateOnlyBrand]);

    useEffect(() => {
        if (isAffiliateOnlyBrand && idWithSlug) {
            nav.replace(window.location.pathname + window.location.search, {
                brandName: constructorBrand?.name,
                backRoute: backRoute,
            });
            setSelectedWayToEarnTab(EarnTypeConstructorFilter.ShopOnline);
        }
    }, [isAffiliateOnlyBrand, constructorBrand?.name, idWithSlug]);

    const userId = useRootSelector((s) => s.store.account.userId);

    const { addProductToCart } = useAddProductToCart();

    const { calculateProductRebate } = useProductRebate();

    const bestRebate = Math.max(
        ...(products || []).map((product) => calculateProductRebate(product).rebate)
    );

    const isShipToCoordinatorEnabled = useRootSelector(selectIsShipToCoordinatorEnabled);

    const eGiftCardProduct =
        products && products.find((prod) => prod.type === ProductType.EGiftCard);
    const physicalCardProduct =
        products && products.find((prod) => prod.type === ProductType.GiftCard);
    const reloadCardProduct = products && products.find((prod) => prod.type === ProductType.Reload);
    const hidePhysicalTab =
        physicalCardProduct &&
        (reloadCardProduct || eGiftCardProduct) &&
        !isShipToCoordinatorEnabled &&
        !physicalCardProduct.isShipToHomeEligible;

    const setQueryParam = useCallback((type: ProductQueryParam) => {
        const url = new URL(window.location.href);
        url.searchParams.set('type', type);
        window.history.pushState(null, '', url.toString());
    }, []);

    const checkQueryParams = (queryParam: ProductQueryParam) => {
        return Object.values(ProductQueryParam).includes(queryParam);
    };

    const checkSelectedTab = useCallback(() => {
        switch (selectedTab) {
            case ProductType.GiftCard:
                return ProductQueryParam.GiftCard;
            case ProductType.EGiftCard:
                return ProductQueryParam.EGiftCard;
            case ProductType.Reload:
                return ProductQueryParam.Reload;
            default:
                return hidePhysicalTab ? ProductQueryParam.EGiftCard : ProductQueryParam.GiftCard;
        }
    }, [hidePhysicalTab, selectedTab]);

    useEffect(() => {
        if (params.get('type') && selectedTab) {
            setQueryParam(checkSelectedTab());
        }
    }, [checkSelectedTab, selectedTab, setQueryParam, params]);

    useEffect(() => {
        const url = new URL(window.location.href);
        const queryParam = url.searchParams.get('type') as ProductQueryParam;
        const defaultTab = checkQueryParams(queryParam) ? queryParam : checkSelectedTab();

        if (products && products.length > 0) {
            if (defaultTab === ProductQueryParam.GiftCard) {
                if (products.find((p) => p.type === ProductType.GiftCard) && !hidePhysicalTab) {
                    return setSelectedTab(ProductType.GiftCard);
                }
            }
            if (defaultTab === ProductQueryParam.EGiftCard) {
                if (products.find((p) => p.type === ProductType.EGiftCard)) {
                    return setSelectedTab(ProductType.EGiftCard);
                }
            }
            if (
                products.find((p) => p.type === ProductType.Reload) &&
                (defaultTab === ProductQueryParam.Reload || hidePhysicalTab)
            ) {
                return setSelectedTab(ProductType.Reload);
            }
            if (!products.find((p) => p.type === ProductType.GiftCard)) {
                return setSelectedTab(ProductType.EGiftCard);
            }
        }
    }, [products, hidePhysicalTab, checkSelectedTab]);

    useEffect(() => {
        if (userId) {
            requestWallet(userId)(dispatch);
        }
    }, [userId, dispatch]);

    const term = useRootSelector((state) => state.store.search.term);

    const handleAddItemToCart = useCallback(
        async (
            product: Product,
            amount: ProductAmountInputFormData,
            giftDetail?: CartLineItemGiftDetails
        ) => {
            const price =
                amount.priceOverride && amount.priceOverride > 0
                    ? amount.priceOverride
                    : demonetize(Object.values(amount.selectedOptions)[0]) || 0;
            const total = price * amount.quantity;

            // @ts-ignore
            constructorInstance.tracker.requests.sendTrackingEvents = true;
            constructorInstance.tracker.trackConversion(term || 'TERM_UNKNOWN', {
                itemId: product.id.toString(),
                itemName: product.name,
                revenue: total,
                type: 'add_to_cart',
                section: 'Products',
            });

            return await addProductToCart(
                product,
                amount,
                brand,
                giftDetail,
                !amount.hideAddedToCartAnimation
            );
        },
        [addProductToCart, brand]
    );

    const renderTerms = () => {
        return (
            <>
                {brands && brands.contentfulBrandsById[brandId] ? (
                    <div className="w-full border-t-2 border-brand">
                        <ProductDetailTerms
                            brandId={brandId}
                            productType={
                                selectedTab === ProductType.EGiftCard
                                    ? ProductType.EGiftCard
                                    : ProductType.GiftCard
                            }
                            defaultExpandedFilter={getGeneralInformationFilter}
                        />
                    </div>
                ) : (
                    <Skeleton className={ProductDetailScreenStyles.termsSkeleton} />
                )}
            </>
        );
    };

    const optionalLoggedInDescription = (
        brandId ? brands?.contentfulBrandsById[brandId]?.optionalLoggedInDescription : null
    ) as Document | null;

    const optionalLoggedInDescriptionComponents = optionalLoggedInDescription
        ? documentToReactComponents(optionalLoggedInDescription)
        : null;

    return (
        <div>
            <div className={ProductDetailScreenStyles.body} key={brandId}>
                <main className={ProductDetailScreenStyles.main}>
                    <section className={ProductDetailScreenStyles.firstRow}>
                        <FirstRow
                            brand={brand || constructorBrand}
                            isLoadingBrandInfo={isLoadingBrandInfo}
                            userId={userId}
                        />
                    </section>

                    {optionalLoggedInDescription && (
                        <section className={ProductDetailScreenStyles.optionalLoggedInDescription}>
                            <div
                                className={
                                    readMoreExpanded
                                        ? ProductDetailScreenStyles.readMoreExpanded
                                        : ProductDetailScreenStyles.readMoreCollapsed
                                }
                            >
                                {optionalLoggedInDescriptionComponents}
                            </div>

                            {!readMoreExpanded && (
                                <button
                                    onClick={() => setReadMoreExpanded(!readMoreExpanded)}
                                    className={ProductDetailScreenStyles.readMoreButton}
                                >
                                    Read More <FontAwesomeIcon icon={faChevronDown} />
                                </button>
                            )}
                        </section>
                    )}

                    {constructorBrand ? (
                        <ProductDetailMainTabAffiliate
                            brandId={brandId}
                            brandName={constructorBrand.name}
                            brandImageUrl={constructorBrand.imageUrl}
                            brandRebate={bestRebate}
                            products={products || []}
                            handleAddItemToCart={handleAddItemToCart}
                            selectedTab={selectedTab}
                            onSelectedTabChange={setSelectedTab}
                            hidePhysicalTab={hidePhysicalTab}
                            onSelectedEarnTypeTabChange={setSelectedWayToEarnTab}
                            selectedEarnTypeTab={selectedWayToEarnTab}
                            constructorBrand={constructorBrand}
                            isAffiliateOnlyBrand={isAffiliateOnlyBrand}
                            isGiftCardOnlyBrand={!!isGiftCardOnlyBrand}
                        />
                    ) : (
                        <Skeleton className={ProductDetailScreenStyles.productDetail} />
                    )}
                    {selectedWayToEarnTab === EarnTypeConstructorFilter.GiftCards
                        ? renderTerms()
                        : null}
                </main>
            </div>
        </div>
    );
};
